import * as jsts from 'jsts/dist/jsts';

import { AlignmentRules } from '../alignmentRules';
import { Bounding } from '../../../core/models/bounding';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentBase } from '../equipment-base';
import { Extension, ExtensionType, RoundingParams } from '../../../equipment/models/elements/extension';
import { Figure } from '../../../equipment/models/figures/figure';
import { PlaneElement } from './planeElement';
import { Point } from '../../../core/models/point';
import { ProductErrors, ProductGroup } from '../product-group';
import { Trapeze } from '../../../equipment/models/figures/trapeze';

import { Utils } from '../../../core/services/utils.service';
import { GlobalSettings } from '../../../core/models/globalSettings';

const DISTANCE_EPSILON: number = .01,
	GEOMETRY_FACTORY: jsts.geom.GeometryFactory = new jsts.geom.GeometryFactory(),
	HEIGHT_EPSILON: number = .01;

export class PalletElement extends PlaneElement {

	get anchorZCoordinate(): number {
		return this.layoutBoundingZ.min;
	}

	get canHaveSiblings(): boolean {
		const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);
		const canHaveSiblings = [
			this.shapes[0].type != 'trapeze',
			!rounding.active || (rounding.active && !rounding.params.leftSideRadius) || (rounding.active && !rounding.params.rightSideRadius)
		];

		return canHaveSiblings.every((condition: boolean) => condition);
	}

	get getSiblingElements(): Array<PalletElement> {
		let siblings: Array<PalletElement> = [];

		if (!this.canHaveSiblings) return [];

		const possibleSiblings: Array<PalletElement> = this.stand.availableElements
			.filter((el: Element) => el.constructor.name == this.constructor.name && (el as PalletElement).canHaveSiblings)
			.filter((el: PalletElement) => {
				const maxDiff: number = Math.abs(el.getBoundingY().max - this.getBoundingY().max),
					minDiff: number = Math.abs(el.getBoundingY().min - this.getBoundingY().min);

				return el != this && (maxDiff < HEIGHT_EPSILON && minDiff < HEIGHT_EPSILON);
			}) as Array<PalletElement>;

		if (possibleSiblings.length) {
			[this, ...possibleSiblings]
				.sort((a: PalletElement, b: PalletElement) => {
					return a.shapes[0].getBoundingX().min - b.shapes[0].getBoundingX().min;
				})
				.forEach((element: PalletElement) => {
					if (!siblings?.length) {
						siblings = [element];
						return;
					}

					const prevElement: PalletElement = siblings[siblings.length - 1];
					const diff: number = Math.abs(element.getBoundingX().min - prevElement.getBoundingX().max);
					const elementRounding: Extension<RoundingParams> = element.getExtension<RoundingParams>(ExtensionType.Rounding);
					const rounded: boolean = elementRounding?.active && !!elementRounding.params.leftSideRadius;
					const prevElementRounding: Extension<RoundingParams> = prevElement.getExtension<RoundingParams>(ExtensionType.Rounding);
					const prevRounded: boolean = prevElementRounding?.active && !!prevElementRounding.params.rightSideRadius;

					if (diff < DISTANCE_EPSILON && !rounded && !prevRounded) {
						siblings.push(element);
					} else {
						if (!siblings.some((el: PalletElement) => el == this)) siblings = [element];
					}
				});
			siblings = siblings.filter((el: PalletElement) => el != this);
		} else {
			siblings = [];
		}
		return siblings;
	}

	get getSiblings(): Array<PalletElement> {
		const yBoundingElement: Bounding = this.getBoundingY();
		return this.stand.availableElements
			.filter((el: Element) => el.constructor.name == this.constructor.name)
			.filter((el: PalletElement) => {
				const maxDiff: number = Math.abs(el.getBoundingY().max - yBoundingElement.max),
					minDiff: number = Math.abs(el.getBoundingY().min - yBoundingElement.min);

				return el != this && maxDiff < HEIGHT_EPSILON && minDiff < HEIGHT_EPSILON;
			})
			.sort((a: PalletElement, b: PalletElement) => {
				return a.shapes[0].getBoundingX().min - b.shapes[0].getBoundingX().min;
			}) as Array<PalletElement>;
	}

	get linearMeters(): number {
		if (this.isExtensionActive(ExtensionType.Rounding)) {
			const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);
			const leftSideRadius: number = Utils.convertPercentToRadius(rounding.params.leftSideRadius, this);
			const rightSideRadius: number = Utils.convertPercentToRadius(rounding.params.rightSideRadius, this);
			const radiusSum: number = Utils.round(leftSideRadius - rightSideRadius)
			return Utils.round(this.Width - radiusSum + Math.PI * radiusSum / 2);
		}

		return this.Width;
	}

	constructor(element: Element) {
		super(element);
	}

	canDrop(product: ProductGroup): boolean {
		return !product.isLooseForm;
	}

	copy(): PalletElement {
		const shapes: Array<Figure> = this.shapes.map((shape: Figure) => shape.copy()),
			extensions: Array<Extension<any>> = this.extensions.map((extension: Extension<any>) => extension.copy()),
			element: Element = new Element(this.parent, shapes, extensions, this.depend, this.id, this.idx);

		return new PalletElement(element);
	}

	getDroppedProduct(x: number, y: number, z: number, activeProducts: Array<ProductGroup> = [], product?: ProductGroup): ProductGroup {
		let products: Array<ProductGroup> = [this, ...this.getSiblingElements]
			.reduce((products: Array<Array<ProductGroup>>, el: PalletElement) => [...products, ...el.products], [])
			.find((group: Array<ProductGroup>) => {
				return Element.calculateGroupMin(group) <= x && Element.calculateGroupMax(group) > x;
			});
		if (!products) return null;

		let zProducts: Array<ProductGroup> = Utils.groupByChildProperty(products, 'begin', 'z')
				.sort((a: ProductGroup, b: ProductGroup) => a[0].begin.z - b[0].begin.z)[0],
			existDepend: Array<ProductGroup> = zProducts
				.filter((pg: ProductGroup) => pg.dependProduct)
				.map((pg: ProductGroup) => pg.dependProduct);

		if (product) {
			const topActiveProducts: Array<ProductGroup> = activeProducts.filter((pg: ProductGroup) => {
				return pg.getBoundingY().max >= product.getBoundingY().max;
			});

			zProducts = zProducts.filter((pg: ProductGroup) => pg !== product && !topActiveProducts.includes(pg));

			if (product.dependProduct) {
				existDepend = existDepend.filter((pg: ProductGroup) => pg != product.dependProduct);
			}
		}

		zProducts = zProducts.filter((pg: ProductGroup) => {
			return pg.getBoundingY().max < y && !existDepend.includes(pg);
		});

		return zProducts.length ? zProducts[0] : null;
	}

	getPointedElement(x: number, y: number, stand: EquipmentBase): PalletElement {
		const elements: Array<PalletElement> = stand.allElements
			.filter((el: Element) => el.depend == this) as Array<PalletElement>;

		for (let el of elements) {
			for (let shape of el.shapes) {
				if (shape.isPointOn(x, null, y)) return el;
			}
		}

		return this;
	}

	hasTrapezeIntersections(product: ProductGroup): boolean {
		const shelfPoly: jsts.geom.Polygon = GEOMETRY_FACTORY.createPolygon(
			(this.shapes[0] as Trapeze).coordinatesZ.map((point: Point) => new jsts.geom.Coordinate(point.x, point.y))
		);

		return !shelfPoly.contains(product.zPolygon);
	}

	normalize(stand: EquipmentBase, segments: Array<Bounding> = []): void {
		const siblings: Array<PalletElement> = this.getSiblingElements,
			palletSegments: Array<Bounding> = segments.length ? segments :
				stand.alignment !== 'default' ? this.getBlockSegments : this.getSiblingSegments(),
			palletLimits: Bounding = this.getSiblingSegments()[0];

		let zGroups: Array<Array<ProductGroup>> = [this, ...siblings]
			.reduce((products: Array<Array<ProductGroup>>, el: PalletElement) => [...products, ...el.products], []);

		if (!zGroups.length) return;

		// sort products and shelf segments according to selected alignment
		if (stand.alignment === 'right') {
			zGroups.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
				return Element.calculateGroupMax(b) - Element.calculateGroupMax(a);
			});
			palletSegments.sort((a: Bounding, b: Bounding) => b.max - a.max);
		} else {
			zGroups.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
				return Element.calculateGroupMin(a) - Element.calculateGroupMin(b);
			});
		}

		if (!stand.arbitraryLayout) {
			zGroups = this.filterArbitraryProducts(zGroups);

			palletSegments.forEach((segment: Bounding) => {
				// filter products which belongs to current segment, find previous product on pallets
				const segGroups: Array<Array<ProductGroup>> = zGroups.filter((group: Array<ProductGroup>, idx: number) => {
						const align: AlignmentRules = Element.alignmentRules(zGroups, idx, segment);
						return align[stand.alignment].checkAffiliation(palletLimits);
					}),
					firstGroupIndex: number = segGroups.length
						? zGroups.findIndex((group: Array<ProductGroup>) => group == segGroups[0])
						: 0,
					prevProduct: Bounding = firstGroupIndex
						? {
							min: Element.calculateGroupMin(zGroups[firstGroupIndex - 1]),
							max: Element.calculateGroupMax(zGroups[firstGroupIndex - 1])
						}
						: null;
				segGroups.forEach((group: Array<ProductGroup>, idx: number) => {
					let align: AlignmentRules = Element.alignmentRules(segGroups, idx, segment),
						beginPoint: number = !idx ? align[stand.alignment].startPoint(prevProduct) : align[stand.alignment].nextPoint();

					// set default angle for product group
					group.forEach((pg: ProductGroup) => {
						pg.angle = GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;
					});

					// set correct x coordinate for product group
					group.forEach((pg: ProductGroup) => {
						pg.begin.x = isFinite(beginPoint) ? beginPoint : align[stand.alignment].startPoint();
					});

					// set new depend element for product group
					if (group[0].element.constructor.name == this.constructor.name) {
						const xBoundingElement: Bounding = group[0].element.getBoundingX(),
							groupMin: number = Element.calculateGroupMin(group),
							groupMax: number = Element.calculateGroupMax(group);

						if (groupMax > xBoundingElement.max || groupMin < xBoundingElement.min) {
							const siblings: Array<Element> = group[0].element.getSiblingElements,
								possibleElement: Element = siblings.find((el: Element) => {
									return el.getBoundingX().max >= align[stand.alignment].checkDependingPoint() &&
										el.getBoundingX().min <= align[stand.alignment].checkDependingPoint();
								});
							if (possibleElement) group.forEach((pg: ProductGroup) => {
								const saveZ: number = pg.begin.z;
								pg.Element = possibleElement;
								pg.begin.z = saveZ;
							});
						}
					}

					// set correct z coordinate for product group
					group
						.filter((pg: ProductGroup) => !pg.dependProduct)
						.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingZ().max - b.getBoundingZ().max || 1)
						.forEach((pg: ProductGroup, idx: number, list: Array<ProductGroup>) => {
							pg.begin.y = pg.element.getBoundingY().max;
							pg.begin.z = Utils.round((!idx ? pg.element.getAbsoluteBoundingZ().min :
								list[idx - 1].getBoundingZ().max) + pg.Depth);
						});

					group
						.filter((pg: ProductGroup) => pg.dependProduct)
						.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
						.forEach((pg: ProductGroup) => {
							pg.begin.y = pg.dependProduct.getBoundingY().max;
							pg.begin.z = pg.dependProduct.getBoundingZ().max;
						});
				});
			});
		}

		// highlight product group when error
		[this, ...siblings].forEach((el: Element) => (el as PalletElement).setProductErrors(palletLimits));
	}

	protected findPriorProduct(group: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): ProductGroup {
		if (list.length == 1) {
			const bounding: Bounding = { min: point.x, max: Utils.round(point.x + group[0].Width) };
			const sameXProducts: Array<Array<ProductGroup>> = this.findSameXProducts(group, bounding);

			if (sameXProducts.length) {
				const groupDepth: number = group
					.filter((pg: ProductGroup) => !pg.dependProduct)
					.reduce((depth: number, pg: ProductGroup) => Utils.round(depth + pg.Depth), 0);
				return sameXProducts[0]
					.sort((a: ProductGroup, b: ProductGroup) => b.getBoundingZ().min - a.getBoundingZ().min)
					.find((pg: ProductGroup) => pg.getBoundingZ().max < Utils.round(point.z - groupDepth));
			}
		}

		return null;
	}

	protected adjustPointToPriorProduct(priorPg: ProductGroup, group: Array<ProductGroup>, point: Point): void {
		const groupDepth: number = group
			.filter((pg: ProductGroup) => !pg.dependProduct)
			.reduce((depth: number, pg: ProductGroup) => Utils.round(depth + pg.Depth), 0);

		point.x = priorPg.begin.x;
		point.y = this.getBoundingY().max;
		point.z = Utils.round(priorPg.getBoundingZ().max + groupDepth);
	}

	protected getItemsZCoordinates(productGroups: Array<ProductGroup>, product: ProductGroup): Array<Array<Point>> {
		const productCoordinates: Array<Array<Point>> = super.getItemsZCoordinates(productGroups, product);
		return [...productCoordinates, this.zCoordinates];
	}

	protected setProductErrors(limits: Bounding): void {
		this.products.forEach((group: Array<ProductGroup>) => {
			group.forEach((pg: ProductGroup) => {
				pg.error = false;

				const xBounding: Bounding = pg.getBoundingX(),
					errors: { [key in ProductErrors]?: boolean } = {
						[ProductErrors.ShelfDepth]: pg.getBoundingZ().min < this.getAbsoluteBoundingZ().min
							|| pg.getBoundingZ().max > this.getAbsoluteBoundingZ().max,
						[ProductErrors.ShelfLeftLimit]: xBounding.min < limits.min,
						[ProductErrors.ShelfRightLimit]: xBounding.max > limits.max,
						[ProductErrors.ProductIntersection]: this.intersectsProduct(pg),
						[ProductErrors.LooseOnShelf]: pg.isLooseForm
					};

				if (pg.dependProduct && !this.stand.arbitraryLayout) {
					errors[ProductErrors.DependLimits] = pg.dependProduct.Width < pg.Width;
					errors[ProductErrors.DependDepth] = pg.dependProduct.Depth < pg.Depth;
				}

				if (pg.element.isExtensionActive(ExtensionType.Rounding)) {
					const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);

					if (rounding.params.leftSideRadius) {
						errors[ProductErrors.ShelfLeftLimit] = xBounding.min < pg.element.getBoundingX().min
							|| this.hasRoundingIntersections(pg, 'left');
					}

					if (rounding.params.rightSideRadius) {
						errors[ProductErrors.ShelfRightLimit] = xBounding.max > pg.element.getBoundingX().max
							|| this.hasRoundingIntersections(pg, 'right');
					}
				}

				if (pg.element.shapes[0].type === 'trapeze') {
					errors[ProductErrors.TrapezeLimits] = this.hasTrapezeIntersections(pg)
						&& !errors[ProductErrors.ShelfDepth]
						&& !errors[ProductErrors.ShelfLeftLimit]
						&& !errors[ProductErrors.ShelfRightLimit];
				}

				pg.error = Object.values(errors).some((error: boolean) => error);
				pg.errorTypes = Object.keys(errors).filter((key: ProductErrors) => errors[key]);
			});
		});
	}

	public setProductPlacementHeight(pg: ProductGroup, placementHeight: number) {
		pg.placementHeight = placementHeight || 1;
	}
}
